import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Star, Clock, ChevronRight, Navigation, Crown, Plus, MessageCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useLieux } from '../hooks/useLieux';
import { rechercher, getType, TYPES_RACCOURCIS, ouvrirNavigation, localisation, APP_NAME } from '../lib/lieux';
import { TypeIcone } from '../components/Lieu';

const LigneLieu = ({ lieu, onClick }) => (
  <div className="ligne-lieu">
    <button className="ligne-lieu-zone" onClick={onClick}>
      <TypeIcone type={lieu.type} />
      <span className="ligne-lieu-texte">
        <strong>{lieu.nom || lieu.code_cau}</strong>
        <span className="ligne-lieu-meta">
          <span className="cau-petit">{lieu.code_cau}</span>
          {localisation(lieu) && <span className="texte-secondaire">{localisation(lieu)}</span>}
        </span>
      </span>
    </button>
    <button className="btn-icone btn-icone-vert" onClick={() => ouvrirNavigation(lieu)} aria-label={`Y aller : ${lieu.nom}`}>
      <Navigation size={20} />
    </button>
  </div>
);

export default function Accueil() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { lieux, indexes, favoris, principal, lieuxRecents, config, loading, marquerConsulte } = useLieux();
  const [q, setQ] = useState('');

  const resultats = useMemo(() => (q.trim() ? rechercher(indexes, q).slice(0, 6) : []), [indexes, q]);

  const ouvrir = (lieu) => {
    marquerConsulte(lieu.id);
    navigate(`/lieux?id=${lieu.id}`);
  };

  const lancerRecherche = (e) => {
    e.preventDefault();
    if (resultats.length === 1) ouvrir(resultats[0]);
    else navigate(`/recherche?q=${encodeURIComponent(q)}`);
  };

  const prenom = (user?.nom || '').split(' ')[0];
  const limiteAtteinte = config && config.limite !== null && config.total >= config.limite;
  const wa = config?.whatsapp_number ? config.whatsapp_number.replace(/\D/g, '') : null;

  return (
    <div className="page">
      <section className="accueil-hero">
        <p className="accueil-salut">{prenom ? `Bonjour ${prenom}` : APP_NAME}</p>
        <h1>Retrouvez facilement vos lieux.</h1>

        <form className="recherche recherche-hero" onSubmit={lancerRecherche} role="search">
          <Search size={22} className="recherche-icone" aria-hidden="true" />
          <input
            type="search"
            inputMode="search"
            placeholder="Où voulez-vous aller ?"
            value={q}
            onChange={e => setQ(e.target.value)}
            aria-label="Rechercher un lieu, un nom ou un CAU"
            autoComplete="off"
          />
          {q && <button type="button" className="btn-icone" onClick={() => setQ('')} aria-label="Effacer"><X size={18} /></button>}
        </form>

        {q.trim() && (
          <div className="resultats-rapides">
            {resultats.length === 0
              ? <p className="texte-secondaire resultats-vide">Aucun lieu ne correspond à « {q} ».</p>
              : resultats.map(l => <LigneLieu key={l.id} lieu={l} onClick={() => ouvrir(l)} />)}
          </div>
        )}
      </section>

      {!loading && lieux.length > 0 && (
        <div className="raccourcis">
          {TYPES_RACCOURCIS.map(id => {
            const t = getType(id);
            return (
              <button key={id} className="raccourci" onClick={() => navigate(`/lieux?type=${id}`)}>
                <span className="raccourci-icone"><t.icon size={20} /></span>
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {principal && (
        <section className="bloc">
          <div className="carte-principal">
            <button className="carte-principal-zone" onClick={() => ouvrir(principal)}>
              <span className="badge-principal"><Crown size={12} /> Lieu principal</span>
              <strong>{principal.nom}</strong>
              <span className="cau-petit">{principal.code_cau}</span>
            </button>
            <button className="btn btn-primaire" onClick={() => ouvrirNavigation(principal)}><Navigation size={18} /> Y aller</button>
          </div>
        </section>
      )}

      {favoris.length > 0 && (
        <section className="bloc">
          <div className="bloc-tete">
            <h2><Star size={18} className="icone-or" /> Mes favoris</h2>
            <button className="lien" onClick={() => navigate('/favoris')}>Tout voir <ChevronRight size={16} /></button>
          </div>
          <div className="carte-blanche">
            {favoris.slice(0, 4).map(l => <LigneLieu key={l.id} lieu={l} onClick={() => ouvrir(l)} />)}
          </div>
        </section>
      )}

      {lieuxRecents.length > 0 && (
        <section className="bloc">
          <div className="bloc-tete">
            <h2><Clock size={18} /> Récemment consultés</h2>
          </div>
          <div className="carte-blanche">
            {lieuxRecents.slice(0, 4).map(l => <LigneLieu key={l.id} lieu={l} onClick={() => ouvrir(l)} />)}
          </div>
        </section>
      )}

      {loading && (
        <section className="bloc">
          <div className="carte-blanche">
            {[0, 1, 2].map(i => <div key={i} className="ligne-lieu"><div className="sk sk-ligne" style={{ width: `${70 - i * 12}%` }} /></div>)}
          </div>
        </section>
      )}

      {!loading && (
        <section className="bloc">
          <div className="carte-blanche resume">
            <div>
              <strong>{lieux.length} lieu{lieux.length > 1 ? 'x' : ''} enregistré{lieux.length > 1 ? 's' : ''}</strong>
              {config && config.limite !== null && (
                <p className="texte-secondaire">Offre gratuite : {config.total} / {config.limite} lieux utilisés</p>
              )}
            </div>
            {lieux.length > 0
              ? <button className="lien" onClick={() => navigate('/lieux')}>Mes lieux <ChevronRight size={16} /></button>
              : <button className="btn btn-primaire" onClick={() => navigate('/lieux/nouveau')}><Plus size={18} /> Ajouter un lieu</button>}
          </div>
          {limiteAtteinte && wa && (
            <a className="bandeau-upgrade" href={`https://wa.me/${wa}?text=${encodeURIComponent(`Je souhaite passer à l'offre payante ${APP_NAME}`)}`} target="_blank" rel="noreferrer">
              <MessageCircle size={18} /> Limite atteinte : contactez-nous sur WhatsApp pour enregistrer plus de lieux
            </a>
          )}
        </section>
      )}
    </div>
  );
}
